import { z } from "zod";
import { AgencyIdSchema, ProjectIdSchema, RevisionIdSchema, RunIdSchema, } from "../primitives/ids.js";
import { TraceContextSchema } from "./trace.js";
/** generation-run.v1.0 — D-028, D-036 */
/**
 * RETRIED remains in the enum for historical readability and v1 compatibility.
 * Step 5 new runs (`runContractVersion` >= 2) must not use the same-row
 * FAILED → RETRIED → PENDING path; infrastructure retry creates a new run.
 */
export const RunStatusSchema = z.enum([
    "PENDING",
    "GENERATING",
    "GENERATED",
    "QA_QUEUED",
    "QA_RUNNING",
    "QA_PASSED",
    "QA_PASSED_WITH_WARNINGS",
    "QA_FAILED",
    "EXPORT_READY",
    "EXPORTING",
    "EXPORTED",
    "SUPERSEDED",
    "FAILED",
    "RETRIED",
    "CANCELLED",
]);
export const NON_TERMINAL_RUN_STATUSES = [
    "PENDING",
    "GENERATING",
    "GENERATED",
    "QA_QUEUED",
    "QA_RUNNING",
    "QA_PASSED",
    "QA_PASSED_WITH_WARNINGS",
    "EXPORT_READY",
    "EXPORTING",
];
/**
 * Legacy v1 supersession set (D-036 / Tier 0 Step 4 v5).
 * EXPORTING intentionally excluded — create-run is blocked instead if EXPORTING exists.
 *
 * Step 5 (`runContractVersion` >= 2) must use
 * `RUN_STATUSES_SUPERSEDED_ON_NEW_RUN_CREATE_V2` from `./step5-generation.js`
 * (unprotected statuses only — does not supersede QA_PASSED / EXPORT_READY / etc.).
 */
export const RUN_STATUSES_SUPERSEDED_ON_NEW_RUN_CREATE = [
    "PENDING",
    "GENERATING",
    "GENERATED",
    "QA_QUEUED",
    "QA_RUNNING",
    "QA_FAILED",
    "QA_PASSED",
    "QA_PASSED_WITH_WARNINGS",
    "EXPORT_READY",
];
export const PackPinSchema = z.object({
    recipeId: z.string().min(1),
    industryPackId: z.string().min(1),
    industryPackVersion: z.string().min(1),
    designPackId: z.string().min(1),
    designPackVersion: z.string().min(1),
    surfacePackId: z.string().min(1),
    surfacePackVersion: z.string().min(1),
    packResolverTimestamp: z.string().datetime(),
});
export const GenerationRunV1Schema = z
    .object({
    schemaVersion: z.literal("generation-run.v1.0"),
    runId: RunIdSchema,
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    businessGraphRevisionId: RevisionIdSchema,
    status: RunStatusSchema,
    packPin: PackPinSchema,
    artifactSetHash: z.string().optional(),
    trace: TraceContextSchema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
})
    .strict();
const ALLOWED_TRANSITIONS = {
    PENDING: ["GENERATING", "SUPERSEDED", "FAILED", "CANCELLED"],
    GENERATING: ["GENERATED", "SUPERSEDED", "FAILED", "CANCELLED"],
    GENERATED: ["QA_QUEUED", "SUPERSEDED", "FAILED", "CANCELLED"],
    QA_QUEUED: ["QA_RUNNING", "SUPERSEDED", "FAILED", "CANCELLED"],
    QA_RUNNING: ["QA_PASSED", "QA_PASSED_WITH_WARNINGS", "QA_FAILED", "SUPERSEDED", "FAILED", "CANCELLED"],
    QA_PASSED: ["EXPORT_READY", "SUPERSEDED", "FAILED", "CANCELLED"],
    // #8 Option A: warn-pass runs are shippable and exportable; WARN reasons are
    // carried into the export bundle quality-gate manifest.
    QA_PASSED_WITH_WARNINGS: ["EXPORT_READY", "SUPERSEDED", "FAILED", "CANCELLED"],
    EXPORT_READY: ["EXPORTING", "SUPERSEDED", "FAILED", "CANCELLED"],
    EXPORTING: ["EXPORTED", "FAILED", "CANCELLED"],
    FAILED: ["RETRIED", "CANCELLED"],
    RETRIED: ["PENDING", "CANCELLED"],
};
export function canTransitionRunStatus(from, to) {
    if (from === to) {
        return true;
    }
    if (from === "SUPERSEDED" || from === "CANCELLED" || from === "EXPORTED") {
        return false;
    }
    if (from === "FAILED") {
        return to === "RETRIED" || to === "CANCELLED";
    }
    if (from === "RETRIED") {
        return to === "PENDING" || to === "CANCELLED";
    }
    const allowed = ALLOWED_TRANSITIONS[from];
    return allowed?.includes(to) ?? false;
}
/**
 * Step 5 versioned transition guard.
 * For `runContractVersion` >= 2: disallows FAILED → RETRIED and RETRIED → PENDING
 * (infrastructure retry creates a new run with retryOfRunId instead).
 * Historical runs (version < 2) keep the legacy same-row RETRIED path.
 */
export function canTransitionRunStatusV2(from, to, runContractVersion) {
    if (runContractVersion >= 2) {
        if (from === "FAILED" && to === "RETRIED") {
            return false;
        }
        if (from === "RETRIED" && to === "PENDING") {
            return false;
        }
    }
    return canTransitionRunStatus(from, to);
}
export function assertRunTransition(from, to) {
    if (!canTransitionRunStatus(from, to)) {
        throw new Error(`Invalid generation-run transition: ${from} → ${to}`);
    }
}
/**
 * D-036: new run supersedes prior non-terminal runs for the same project.
 */
export function supersedeNonTerminalRuns(runs, projectId, exceptRunId) {
    const now = new Date().toISOString();
    return runs.map((run) => {
        if (run.projectId !== projectId) {
            return run;
        }
        if (exceptRunId && run.runId === exceptRunId) {
            return run;
        }
        if (!NON_TERMINAL_RUN_STATUSES.includes(run.status)) {
            return run;
        }
        return {
            ...run,
            status: "SUPERSEDED",
            updatedAt: now,
        };
    });
}
/** Export eligibility rules from Pre-Code Hardening. */
export function isRunExportEligible(input) {
    const { run, currentBusinessGraphRevisionId, gateMode, qualityGatePassed, hasBlockingIssue } = input;
    if (run.status !== "EXPORT_READY" && run.status !== "EXPORTING") {
        return false;
    }
    if (run.businessGraphRevisionId !== currentBusinessGraphRevisionId) {
        return false;
    }
    if (gateMode !== "RELEASE") {
        return false;
    }
    if (!qualityGatePassed || hasBlockingIssue) {
        return false;
    }
    return true;
}
//# sourceMappingURL=generation-run.js.map